import React, {useEffect, useState} from 'react';
import {BadgeEuro, CalendarDays, Link2, MapPin, UserRound} from "lucide-react";
import {toast} from "react-toastify";
import PageForm from "components/pageForm";
import SubNavbar from "components/SubNavbar/SubNavbar";
import {ArrowLeft, CrossIcon} from "assets/icons";
import {useNavigate, useParams} from "react-router-dom";
import ApiDataBase from 'server'
import Button from "components/button";
import Tooltip from "components/tooltips/Tooltips";
import {dateToEpoch, dateWordShort} from "helper/helper";
import UseContext from "hooks/useContext";
import {statutMission} from "helper/helper";
import useModal from "../components/modal/useModal";
import RatingTempWorker from "components/ratingTempWorker/ratingTempWorker";
import {formatMissionTasksTitle, getMissionTasks} from "helper/missionTasks";
import AccessDenied from "components/accessDenied/AccessDenied";
import isAccessDenied from "utils/isAccessDenied";
import MissionPerimeterRow from "components/missionPerimeter/MissionPerimeterRow";
import {InfoRow, MissionDescription, MissionSection, MissionText} from "components/missionDetail/MissionDetailParts";
import MissionPlanning from "components/missionDetail/MissionPlanning";
import MissionRecruitment from "components/missionDetail/MissionRecruitment";
import MissionRequirements from "components/missionDetail/MissionRequirements";
import SectorBadge from "components/sectorBadge/SectorBadge";
import {formatFullName} from "helper/personName";

const MissionFiche = () => {
    const {companyData, setObjID, setMissionTitle, setMissionId, setMissionActionData, missionStatusUpdate, setMissionStatusUpdate, setPerimeterModalData, language} = UseContext()
    const { toggle } = useModal();
    const [infoMission, setInfoMission] = useState({})
    const [scheduleWeeks, setScheduleWeeks] = useState([])
    const [isWaiting, setIsWaiting] = useState(true);
    const [assignedUser, setAssignedUser] = useState(null);
    const [isAccessDeniedMission, setIsAccessDeniedMission] = useState(false);

    const statusObj = statutMission(infoMission?.duration)
    const missionTasks = getMissionTasks(infoMission)
    const navigate = useNavigate()
    const {id} = useParams()

    // Mapping du statut vers les couleurs du Chip
    const getStatusConfig = (statusText) => {
        if (statusText?.includes('cours')) return { color: 'SUCCESS', label: 'En cours' };
        if (statusText?.includes('venir')) return { color: 'PRIMARY', label: 'À venir' };
        return { color: 'SECONDARY', label: 'Terminée' };
    }
    const statusConfig = getStatusConfig(statusObj?.text)
    const statusKeyByColor = { SUCCESS: 'inProgress', PRIMARY: 'upcoming', SECONDARY: 'finished' }
    // Badge tones of the temp worker app: success, primary (brand) and neutral.
    const statusBadgeByColor = {
        SUCCESS: 'bg-green-50 text-green-700',
        PRIMARY: 'bg-yellow-50 text-yellow-700',
        SECONDARY: 'bg-gray-100 text-gray-700',
    }

    useEffect(() => {
        setIsWaiting(true)
        setIsAccessDeniedMission(false)
        ApiDataBase.getMissionInfo({id},
            (data) => {
                setInfoMission(data ?? {})
                setIsWaiting(false);
                if (data?.title) setMissionTitle(data.title);
                if (data?.id_mission) setMissionId(data.id_mission);
                
            },
            (err) => {
                console.log(err)
                if (isAccessDenied(err)) {
                    setIsAccessDeniedMission(true)
                    setIsWaiting(false)
                }
            })
    }, [id, setMissionTitle, setMissionId])

    // Silent re-read after a perimeter change: the skeleton would hide the page
    // the user is looking at. On failure the line shows what the PATCH returned.
    const reloadMissionAfterPerimeterChange = (perimeter) => {
        ApiDataBase.getMissionInfo({id},
            (data) => setInfoMission(data ?? {}),
            (err) => {
                console.log(err)
                setInfoMission((prev) => ({
                    ...prev,
                    perimeter: perimeter ? { ...perimeter, archived: false } : null,
                }))
            })
    }

    const openChangePerimeter = () => {
        setPerimeterModalData({
            mission: {
                id: infoMission._id,
                title: infoMission.title,
                number: infoMission.id_mission,
                statusKey: statusKeyByColor[statusConfig.color],
                perimeter: infoMission.perimeter ?? null,
            },
            onChanged: reloadMissionAfterPerimeterChange,
        })
        toggle('mission_perimeter_change')
    }

    useEffect(() => {
        return () => {
            setMissionTitle('');
            setMissionId('');
        };
    }, [setMissionTitle, setMissionId])

    useEffect(() => {
        if (missionStatusUpdate) {
            setInfoMission((prev) => ({ ...prev, status: missionStatusUpdate.status }));
            setMissionStatusUpdate(null);
        }
    }, [missionStatusUpdate, setMissionStatusUpdate])

    // Récupérer les infos de l'intérimaire assigné depuis infoMission
    useEffect(() => {
        if (infoMission?.user_assigned) {
            // Si user_assigned est un objet avec firstname et lastname (nouveau format API)
            if (infoMission.user_assigned.firstname || infoMission.user_assigned.lastname) {
                setAssignedUser({
                    firstname: infoMission.user_assigned.firstname,
                    lastname: infoMission.user_assigned.lastname,
                    _id: infoMission.user_assigned._id || infoMission.user_assigned
                });
            } else {
                // Fallback sur les anciennes sources de données
                const userData = infoMission?.userInfo?.[0] 
                    || infoMission?.user_assigned_infos 
                    || infoMission?.employee?.[0]
                    || null;
                setAssignedUser(userData);
            }
        } else {
            setAssignedUser(null);
        }
    }, [infoMission])

    useEffect(() => {
        ApiDataBase.getScheduleByMission({id: id},
            (data) => setScheduleWeeks(Array.isArray(data) ? data : []),
            (err) => console.log(err))
    }, [infoMission])

    const getMissionAction = (mission) => {
        if (!mission?.duration) return null;
        if (!['online', '', undefined].includes(mission.status)) return null;
        const now = new Date();
        const startDate = new Date(mission.duration.datestart);
        const endDate = new Date(mission.duration.dateend);
        const hasAssigned = !!mission.user_assigned;
        // Compare dates without time using dateToEpoch (resets time to 00:00:00)
        const isToday = dateToEpoch(now).getTime() === dateToEpoch(startDate).getTime();
        const isPast = dateToEpoch(now) > dateToEpoch(endDate);
        const isUpcoming = dateToEpoch(now) < dateToEpoch(startDate) && !isToday;
        const isInProgress = !isUpcoming && !isPast;

        // Upcoming missions can be cancelled (even with an assigned user)
        if (!hasAssigned || isUpcoming) {
            return { type: 'cancel', label: 'Annuler la mission' };
        }
        // In-progress missions (including today) with assigned user must be interrupted
        if (hasAssigned && (isToday || isInProgress)) {
            return { type: 'interrupt', label: 'Interrompre la mission', isStartDay: isToday };
        }
        return null;
    }

    const handleMissionAction = () => {
        const action = getMissionAction(infoMission);
        if (!action) return;
        setObjID(infoMission._id);
        if (action.type === 'cancel') {
            toggle('mission_company_cancel');
        } else if (action.type === 'interrupt') {
            setMissionActionData({ isStartDay: action.isStartDay });
            toggle('mission_company_interrupt');
        }
    }

    // Obtenir les infos de l'intérimaire
    const getAssignedUserInfo = () => {
        if (assignedUser?.firstname || assignedUser?.lastname) {
            return { firstname: assignedUser.firstname, lastname: assignedUser.lastname };
        }
        if (infoMission?.userInfo?.[0]?.firstname || infoMission?.userInfo?.[0]?.lastname) {
            return { firstname: infoMission.userInfo[0].firstname, lastname: infoMission.userInfo[0].lastname };
        }
        if (infoMission?.user_assigned_infos?.firstname || infoMission?.user_assigned_infos?.lastname) {
            return { firstname: infoMission.user_assigned_infos.firstname, lastname: infoMission.user_assigned_infos.lastname };
        }
        if (infoMission?.employee?.[0]?.firstname || infoMission?.employee?.[0]?.lastname) {
            return { firstname: infoMission.employee[0].firstname, lastname: infoMission.employee[0].lastname };
        }
        return null;
    }

    const t = (key, fallback = '') => language.getValue(`missionFiche.${key}`, fallback)
    const fill = (text, values) =>
        Object.entries(values).reduce((result, [name, value]) => result.replace(`{${name}}`, value ?? ''), text)

    const missionNumber = infoMission?.id_mission
    const startDate = infoMission?.duration?.datestart
    const endDate = infoMission?.duration?.dateend
    const period = !startDate && !endDate
        ? t('noDates', 'Dates non précisées')
        : dateWordShort(startDate) === dateWordShort(endDate)
            ? fill(t('periodDay', 'Le {date}'), {date: dateWordShort(startDate)})
            : fill(t('periodRange', 'Du {start} au {end}'), {start: dateWordShort(startDate), end: dateWordShort(endDate)})
    const wage = infoMission?.salary?.wage_per_hour
        ? fill(t('hourlyWageValue', '{amount} €/h'), {amount: infoMission.salary.wage_per_hour})
        : t('wageUnknown', 'Selon expérience')
    const address = infoMission?.address ?? {}
    const city = [address.postcode, address.city].filter(Boolean).join(' ')
    const descriptionHtml = [infoMission?.desc, infoMission?.other].filter(Boolean).join('')
    const dressCode = typeof infoMission?.dressCode === 'string' ? infoMission.dressCode.trim() : ''
    const assignedUserInfo = infoMission?.user_assigned ? getAssignedUserInfo() : null
    const assignedName = assignedUserInfo
        ? formatFullName(assignedUserInfo.firstname, assignedUserInfo.lastname)
        : ''
    const assignedPhone = assignedUser?.phone || assignedUser?.telephone
    // No temp worker yet and starting today or later: the recruitment is still going. Compared by
    // day, so a mission starting today still shows it whatever its start time.
    const startOfToday = new Date()
    startOfToday.setHours(0, 0, 0, 0)
    const isRecruiting = !infoMission?.user_assigned && !!startDate && new Date(startDate) >= startOfToday

    const detailText = (value) => {
        if (typeof value === 'string' && value) return value
        if (value?.message) return value.message
        return null
    }
    const reasons = [
        infoMission?.info_temporary_activity && {
            title: t('reason.temporaryActivity', 'Accroissement temporaire d\'activité'),
            lines: [detailText(infoMission.info_temporary_activity)],
        },
        infoMission?.awaiting_recruitement && {
            title: t('reason.awaitingRecruitment', 'En attente de recrutement'),
            lines: [
                formatFullName(infoMission.awaiting_recruitement.firstname || infoMission.awaiting_recruitement.prenom, infoMission.awaiting_recruitement.lastname || infoMission.awaiting_recruitement.name) &&
                    fill(t('reason.person', 'Personne : {value}'), {value: formatFullName(infoMission.awaiting_recruitement.firstname || infoMission.awaiting_recruitement.prenom, infoMission.awaiting_recruitement.lastname || infoMission.awaiting_recruitement.name)}),
                infoMission.awaiting_recruitement.date &&
                    fill(t('reason.plannedDate', 'Date prévue : {value}'), {value: dateWordShort(infoMission.awaiting_recruitement.date)}),
            ],
        },
        infoMission?.replacement_employee && {
            title: t('reason.replacement', 'Remplacement d\'un salarié absent'),
            lines: [
                formatFullName(infoMission.replacement_employee.firstname || infoMission.replacement_employee.prenom, infoMission.replacement_employee.lastname || infoMission.replacement_employee.name) &&
                    fill(t('reason.replacedEmployee', 'Salarié remplacé : {value}'), {value: formatFullName(infoMission.replacement_employee.firstname || infoMission.replacement_employee.prenom, infoMission.replacement_employee.lastname || infoMission.replacement_employee.name)}),
                infoMission.replacement_employee.poste &&
                    fill(t('reason.position', 'Poste : {value}'), {value: infoMission.replacement_employee.poste}),
                infoMission.replacement_employee.description &&
                    fill(t('reason.motive', 'Motif : {value}'), {value: infoMission.replacement_employee.description}),
            ],
        },
        infoMission?.temporary_contract && {
            title: t('reason.temporaryContract', 'Contrat temporaire d\'usage'),
            lines: [detailText(infoMission.temporary_contract)],
        },
        infoMission?.seasonal_employment && {
            title: t('reason.seasonal', 'Emploi à caractère saisonnier'),
            lines: [detailText(infoMission.seasonal_employment)],
        },
    ].filter(Boolean)
    // Older missions only carry a motif object or a reason string.
    if (!reasons.length && (infoMission?.motif || infoMission?.reason)) {
        reasons.push({
            title: infoMission?.reason || (typeof infoMission?.motif === 'string' ? infoMission.motif : t('reason.title', 'Motif de recrutement')),
            lines: [infoMission?.motif?.message],
        })
    }

    // Public job page of the mission, the one temp workers open from a shared link.
    const missionPublicUrl = missionNumber ? `https://jobbiz.fr/jobs/detail/?id=${missionNumber}` : null
    const canShareMission = isRecruiting && !!missionPublicUrl
    const copyMissionLink = () => {
        navigator.clipboard.writeText(missionPublicUrl).then(
            () => toast.success(t('linkCopied', 'Lien de la mission copié')),
            () => toast.error(t('linkCopyError', 'Le lien n\'a pas pu être copié'))
        )
    }
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([address.street, address.postcode, address.city].filter(Boolean).join(' '))}`

    if (isAccessDeniedMission) {
        return (
            <PageForm>
                <AccessDenied variant={'mission'} onBack={() => navigate('/missions')}/>
            </PageForm>
        );
    }

    return (
        <PageForm>
            {isWaiting ? (
                <div className="animate-pulse">
                    <SubNavbar>
                        <div className="w-full flex items-center justify-between h-full">
                            <div className="h-9 w-24 bg-gray-100 rounded-lg"></div>
                        </div>
                    </SubNavbar>
                    <div className="mx-auto max-w-[720px] px-6 py-8 tab:px-8">
                        <div className="h-4 w-32 bg-gray-100 rounded"></div>
                        <div className="mt-2 h-8 w-2/3 bg-gray-100 rounded"></div>
                        <div className="mt-3 h-6 w-48 bg-gray-100 rounded"></div>
                        <div className="my-[30px] h-px bg-gray-100"></div>
                        <div className="space-y-6">
                            {[0, 1, 2].map((key) => (
                                <div key={key} className="flex items-center gap-3">
                                    <div className="h-[42px] w-[42px] rounded-xl bg-gray-100"></div>
                                    <div className="flex-1 space-y-2">
                                        <div className="h-4 w-24 bg-gray-100 rounded"></div>
                                        <div className="h-4 w-40 bg-gray-100 rounded"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <SubNavbar>
                        <div className="w-full flex items-center justify-between h-full">
                            <div className="flex items-center gap-4">
                                <Button size={'SM'} color={'SECONDARY'} onClick={() => navigate(-1)}>
                                    <div className="flex items-center gap-2">
                                        <ArrowLeft w={15} h={12} color={'#374151'}/>
                                        <span>Retour</span>
                                    </div>
                                </Button>
                            </div>
                            <div className="flex items-center gap-2">
                            {canShareMission && (
                                <Tooltip text={t('copyLink', 'Copier le lien de la mission')} placement="bottom">
                                    <Button
                                        size={'ICON'}
                                        color={'SECONDARY'}
                                        onClick={copyMissionLink}
                                        ariaLabel={t('copyLink', 'Copier le lien de la mission')}
                                    >
                                        <Link2 size={18} color="#374151" aria-hidden="true"/>
                                    </Button>
                                </Tooltip>
                            )}
                            {getMissionAction(infoMission) && (
                                <Button
                                    size={'SM'}
                                    color={'SECONDARY'}
                                    onClick={handleMissionAction}
                                >
                                    <div className="flex items-center gap-2">
                                        <CrossIcon wh={16} color={'#374151'}/>
                                        <span className="hidden sm:inline">{getMissionAction(infoMission).label}</span>
                                    </div>
                                </Button>
                            )}
                            </div>
                        </div>
                    </SubNavbar>

                    {/* Mission sheet laid out as in the temp worker app (jobbiz_app_v2, missions/[id]) */}
                    <div className="flex flex-col items-start gap-10 px-6 py-8 tab:px-8 lg:flex-row lg:justify-center lg:gap-12">
                        <div className="w-full min-w-0 max-w-[720px] flex-1">
                            <header>
                                {missionNumber ? (
                                    <p className="mb-1 text-sm leading-5 text-gray-500 uppercase">
                                        {fill(t('missionNumber', 'Mission n°{number}'), {number: missionNumber})}
                                    </p>
                                ) : null}
                                <h1 className="text-2xl font-bold leading-[30px] tracking-[-0.6px] text-gray-900">
                                    {infoMission?.title || 'Mission'}
                                </h1>
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                    <span className={`badge badge-lg ${statusBadgeByColor[statusConfig.color]}`}>
                                        {t(`status.${statusKeyByColor[statusConfig.color]}`, statusConfig.label)}
                                    </span>
                                    <SectorBadge activity={infoMission?.activity} size="lg" className="max-w-[60%]"/>
                                    <span className="min-w-0 truncate text-sm leading-5 text-gray-500">
                                        {address.city || t('noLocation', 'Lieu non spécifié')}
                                    </span>
                                </div>
                            </header>
                            {isRecruiting ? null : (
                                <div className="flex h-[60px] items-center" aria-hidden="true">
                                    <div className="h-px w-full bg-gray-100"/>
                                </div>
                            )}

                            {isRecruiting ? (
                                <MissionRecruitment
                                    mission={infoMission}
                                    labels={{
                                        published: t('recruitment.published', 'Mission publiée'),
                                        publishedOn: t('recruitment.publishedOn', 'Le {date}'),
                                        recruiting: t('recruitment.recruiting', 'Recrutement en cours'),
                                        searching: t('recruitment.searching', 'Nous recherchons un profil pour cette mission.'),
                                        pendingOne: t('recruitment.pendingOne', '{count} candidature en cours de traitement'),
                                        pendingMany: t('recruitment.pendingMany', '{count} candidatures en cours de traitement'),
                                        assigned: t('recruitment.assigned', 'Intérimaire attribué'),
                                        assignedHint: t('recruitment.assignedHint', "Son nom s'affichera sur cette fiche."),
                                    }}
                                />
                            ) : null}

                            {descriptionHtml ? (
                                <MissionDescription
                                    title={t('description', 'Description')}
                                    html={descriptionHtml}
                                    seeMoreLabel={t('seeMore', 'Voir plus')}
                                    seeLessLabel={t('seeLess', 'Voir moins')}
                                />
                            ) : null}

                            {missionTasks.length > 0 ? (
                                <MissionSection title={formatMissionTasksTitle(infoMission?.title, language)}>
                                    <ul className="list-disc space-y-1 pl-5 text-sm leading-5 text-gray-500">
                                        {missionTasks.map((label, index) => (
                                            <li key={`${index}-${label}`}>{label}</li>
                                        ))}
                                    </ul>
                                </MissionSection>
                            ) : null}


                            {scheduleWeeks.length > 0 ? (
                                <MissionSection>
                                    <MissionPlanning
                                        weeks={scheduleWeeks}
                                        labels={{
                                            title: t('planning.title', 'Planning'),
                                            notWorked: t('planning.notWorked', 'Non travaillé'),
                                            previous: t('planning.previous', 'Semaine précédente'),
                                            next: t('planning.next', 'Semaine suivante'),
                                            range: t('periodRange', 'Du {start} au {end}'),
                                            day: t('periodDay', 'Le {date}'),
                                        }}
                                    />
                                </MissionSection>
                            ) : null}

                            {reasons.length > 0 ? (
                                <MissionSection title={t('reason.title', 'Motif de recrutement')}>
                                    <div className="flex flex-col gap-4">
                                        {reasons.map((reason) => {
                                            const lines = reason.lines.filter(Boolean)
                                            return (
                                                <div key={reason.title}>
                                                    <p className="text-sm font-medium leading-5 text-gray-900">{reason.title}</p>
                                                    {lines.length ? (
                                                        lines.map((line) => <MissionText key={line}>{line}</MissionText>)
                                                    ) : (
                                                        <MissionText>{t('reason.noDetail', 'Aucun détail fourni')}</MissionText>
                                                    )}
                                                </div>
                                            )
                                        })}
                                    </div>
                                </MissionSection>
                            ) : null}

                            <MissionRequirements
                                mission={infoMission}
                                labels={{
                                    tools: t('requirements.tools', 'Matériels'),
                                    licences: t('requirements.licences', 'Permis'),
                                    language: t('requirements.language', 'Langues'),
                                    required: t('requirements.required', 'Requis pour cette mission'),
                                    toolProvidedBy: {
                                        company: t('requirements.toolProvidedBy.company', "Fourni par l'entreprise"),
                                        temp_worker: t('requirements.toolProvidedBy.temp_worker', "Apporté par l'intérimaire"),
                                    },
                                }}
                            />
                            {dressCode ? (
                                <MissionSection title={t('dressCode', 'Tenue vestimentaire')}>
                                    <MissionText className="whitespace-pre-line">{dressCode}</MissionText>
                                </MissionSection>
                            ) : null}
                            {missionNumber && infoMission?.createdAt ? (
                                <p className="text-sm leading-5 text-gray-500">
                                    {fill(t('createdAt', 'Mission n°{number} créée le {date}'), {number: missionNumber, date: dateWordShort(infoMission.createdAt)})}
                                </p>
                            ) : null}
                        </div>

                        {/* Sticky recap: the facts a manager checks while reading the sheet */}
                        <aside className="w-full shrink-0 lg:sticky lg:top-[140px] lg:w-[340px]">
                            <div className="corner-smooth-12 flex flex-col gap-6 border border-gray-100 bg-white p-6">
                                <div className="flex flex-col gap-6">
                                    <InfoRow icon={CalendarDays} label={t('period', 'Période')} value={period}/>
                                    <InfoRow icon={BadgeEuro} label={t('hourlyWage', 'Salaire horaire brut')} value={wage}/>
                                    <InfoRow
                                        icon={UserRound}
                                        label={t('contact', 'Responsable')}
                                        value={infoMission?.contact?.name || t('notProvided', 'Non renseigné')}
                                    />
                                    {infoMission?.hasPerimeters === true && (
                                        <MissionPerimeterRow
                                            perimeter={infoMission.perimeter}
                                            canChange={infoMission.canChangePerimeter === true}
                                            onChange={openChangePerimeter}
                                        />
                                    )}
                                </div>
                                <div className="h-px bg-gray-100" aria-hidden="true"/>
                                <div>
                                    <h2 className="mb-1 text-sm font-semibold leading-5 text-gray-900">{t('tempWorker', 'Intérimaire')}</h2>
                                    {assignedName ? (
                                        <div className="mt-3">
                                            <InfoRow
                                                icon={UserRound}
                                                label={assignedName}
                                                value={assignedPhone || assignedUser?.email}
                                                action={
                                                    <RatingTempWorker
                                                        isTutoCanBeDisplayed={false}
                                                        idMission={infoMission._id}
                                                        idTempWorker={infoMission.user_assigned._id || assignedUser?._id}
                                                        idTimeSheet={null}
                                                    />
                                                }
                                            />
                                        </div>
                                    ) : (
                                        <MissionText>{t('noTempWorker', 'Aucun intérimaire assigné')}</MissionText>
                                    )}
                                </div>
                                {address.street || city ? (
                                    <>
                                        <div className="h-px bg-gray-100" aria-hidden="true"/>
                                        <div>
                                            <h2 className="mb-1 text-sm font-semibold leading-5 text-gray-900">{t('workplace', 'Lieu de travail')}</h2>
                                            {address.street ? <MissionText>{address.street}</MissionText> : null}
                                            {city ? <MissionText className={address.street ? 'mt-1' : ''}>{city}</MissionText> : null}
                                            <Button
                                                size={'SM'}
                                                color={'SECONDARY'}
                                                className="mt-4 inline-flex"
                                                onClick={() => window.open(mapsUrl, '_blank', 'noopener,noreferrer')}
                                            >
                                                <MapPin size={16} color="#374151" aria-hidden="true"/>
                                                {t('openMaps', 'Voir sur Google Maps')}
                                            </Button>
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        </aside>
                    </div>
                </>
            )}
        </PageForm>
    );
};

export default MissionFiche;
